import { definePreset } from '@primevue/themes'
import Aura from '@primevue/themes/aura'

/**
 * SliceAL admin-web design tokens.
 *
 * Single source of truth for the visual system. Feature CSS uses the CSS
 * variables emitted by PrimeVue from this preset (`--p-primary-600`,
 * `--p-surface-0`, `--p-sliceal-*`) and never reaches for raw hex values.
 *
 * ── PALETTE DISCIPLINE ──
 * The numeric `--p-<color>-N` slots PrimeVue requires internally are kept,
 * but they map onto a SMALL set of distinct values per palette:
 *   • Brand (primary): 5 values — soft · light · default · hover · active
 *   • State  (success/warning/danger/info): 3 values — soft · light · default
 *   • Neutrals (--p-surface-*): full Aura ramp, unchanged
 * Devs no longer get a different color for picking `-400` vs `-500`. UI
 * consistency over shade-shopping.
 *
 * File layout:
 *   1. Color ramps   — primary + 4 state ramps with bounded distinct values
 *   2. Light + dark  — text/surface overrides per colorScheme
 *   3. Scales        — radius · motion (typography font-family below)
 *   4. Institutional — sidebar palette, dark-slate accents, decorative branding
 *   5. Badge palette — branded pills (NOT semantic state)
 *   6. Preset assembly — wires everything into PrimeVue's expected shape
 *
 * Adding a new color → extend the matching ramp, don't introduce a 6th ramp.
 * Adding a new role  → alias an existing shade before reaching for a new hex.
 * Adding a new token under --p-sliceal-* → drop it in the right group below;
 *                       it's auto-promoted to a CSS var via `slicealCssVars`.
 */

// ──────────────────────────────────────────────────────────────────────
// 1. COLOR RAMPS — limited palette mapped across the 50–900 slots
// ──────────────────────────────────────────────────────────────────────
//
// Mature design systems (Polaris, Primer, Atlassian) keep large internal
// palettes but expose a small semantic surface to developers. We do the
// same: each ramp below carries only 5 (brand) or 3 (state) distinct
// values, mapped across the 10–11 slots PrimeVue requires internally.
//
// Brand (5 values):    soft · light · default · hover · active
// State (3 values):    soft · light · default
// Neutrals (full):     --p-surface-* inherited from Aura — unchanged
//
// The slot mapping is intentionally coarse — devs picking `primary-400`
// vs `primary-500` no longer pull two different colors. UI consistency
// trumps shade-shopping. If you need a darker variant for text on a
// light bg, propose a 4th named shade for the palette, don't reach for
// 700/800 from a brand ramp.

// ── PRIMARY · 5 distinct values ───────────────────────────────────────
const PRIMARY_SOFT = '#F2F0FF' //     subtle backgrounds, hero washes
const PRIMARY_LIGHT = '#E4DFFF' //    selected rows, soft badges, chip bg
const PRIMARY_DEFAULT = '#6D5EF9' //  primary CTA, active tab, focus ring
const PRIMARY_HOVER = '#5A49E6' //    hover / pressed CTA
const PRIMARY_ACTIVE = '#4939C7' //   active / depressed CTA

const PRIMARY = {
  25: PRIMARY_SOFT,
  50: PRIMARY_SOFT,
  100: PRIMARY_LIGHT,
  200: PRIMARY_LIGHT,
  300: PRIMARY_LIGHT,
  400: PRIMARY_DEFAULT,
  500: PRIMARY_DEFAULT,
  600: PRIMARY_DEFAULT,
  700: PRIMARY_HOVER,
  800: PRIMARY_ACTIVE,
  900: PRIMARY_ACTIVE,
  950: PRIMARY_ACTIVE,
} as const

// ── SUCCESS · 3 distinct values ───────────────────────────────────────
const SUCCESS_SOFT = '#EEFDF7' //     success banner bg, hero wash
const SUCCESS_LIGHT = '#D8F9EB' //    success chip bg, light badge
const SUCCESS_DEFAULT = '#1EA775' //  approved / verified / active state

const SUCCESS = {
  50: SUCCESS_SOFT,
  100: SUCCESS_LIGHT,
  200: SUCCESS_LIGHT,
  300: SUCCESS_LIGHT,
  400: SUCCESS_DEFAULT,
  500: SUCCESS_DEFAULT,
  600: SUCCESS_DEFAULT,
  700: SUCCESS_DEFAULT,
  800: SUCCESS_DEFAULT,
  900: SUCCESS_DEFAULT,
} as const

// ── WARNING · 3 distinct values ───────────────────────────────────────
const WARNING_SOFT = '#FFF9EB'
const WARNING_LIGHT = '#FFF0C9'
const WARNING_DEFAULT = '#D98D00' //  pending / needs-action / flagged

const WARNING = {
  50: WARNING_SOFT,
  100: WARNING_LIGHT,
  200: WARNING_LIGHT,
  300: WARNING_LIGHT,
  400: WARNING_DEFAULT,
  500: WARNING_DEFAULT,
  600: WARNING_DEFAULT,
  700: WARNING_DEFAULT,
  800: WARNING_DEFAULT,
  900: WARNING_DEFAULT,
} as const

// ── DANGER · 3 distinct values ────────────────────────────────────────
const DANGER_SOFT = '#FFF3F3'
const DANGER_LIGHT = '#FFE2E2'
const DANGER_DEFAULT = '#DC2626' //   rejected / blocked / error

const DANGER = {
  50: DANGER_SOFT,
  100: DANGER_LIGHT,
  200: DANGER_LIGHT,
  300: DANGER_LIGHT,
  400: DANGER_DEFAULT,
  500: DANGER_DEFAULT,
  600: DANGER_DEFAULT,
  700: DANGER_DEFAULT,
  800: DANGER_DEFAULT,
  900: DANGER_DEFAULT,
} as const

// ── INFO · 3 distinct values ──────────────────────────────────────────
const INFO_SOFT = '#EEF5FF'
const INFO_LIGHT = '#D9E9FF'
const INFO_DEFAULT = '#2C72DB' //     informational / in-review

const INFO = {
  50: INFO_SOFT,
  100: INFO_LIGHT,
  200: INFO_LIGHT,
  300: INFO_LIGHT,
  400: INFO_DEFAULT,
  500: INFO_DEFAULT,
  600: INFO_DEFAULT,
  700: INFO_DEFAULT,
  800: INFO_DEFAULT,
  900: INFO_DEFAULT,
} as const

// ──────────────────────────────────────────────────────────────────────
// 2. LIGHT + DARK MODE — text + surface overrides per colorScheme
// ──────────────────────────────────────────────────────────────────────
//
// Light-mode surface inherits from Aura's slate-tinted neutral scale.
// Dark mode requires explicit overrides for surface AND lightest shades
// of every semantic ramp (50/100/200) — those are the bg washes that need
// to feel native on a dark surface. Shades 300+ work on both backgrounds.

const LIGHT_TEXT = {
  color: '#0A0A0A', //       body text, headings
  mutedColor: '#414651', //  labels, captions, placeholders
} as const

const DARK_TEXT = {
  color: '#F9FAFB',
  mutedColor: '#D1D5DB',
} as const

const DARK_SURFACE = {
  0: '#0E1014', //    page background
  50: '#15181D', //   subtle elevated bg
  100: '#1B1F26', //  card / chip bg
  200: '#262B33', //  border subtle / raised
  300: '#333944',
  400: '#525964',
  500: '#71717A',
  600: '#A1A1AA',
  700: '#D4D4D8',
  800: '#E4E4E7',
  900: '#F4F4F5',
  950: '#FAFAFA',
} as const

// Dark-mode tints for the lightest shades of each ramp. Mid-and-darker
// shades (300+) reuse the LIGHT ramp values defined above.
const DARK_PRIMARY_TINTS = { 25: '#1A1640', 50: '#1F1A4A', 100: '#2A2363', 200: '#3A2F8A' } as const
const DARK_SUCCESS_TINTS = { 50: '#0F2A1E', 100: '#143A29', 200: '#1A5239' } as const
const DARK_WARNING_TINTS = { 50: '#3A2A0A', 100: '#4D3A0F', 200: '#6B521A' } as const
const DARK_DANGER_TINTS = { 50: '#3A1414', 100: '#4D1A1A', 200: '#6B2424' } as const
const DARK_INFO_TINTS = { 50: '#0F1F3A', 100: '#152A52', 200: '#1D3C7A' } as const

// ──────────────────────────────────────────────────────────────────────
// 3. SCALES — radius · motion
// ──────────────────────────────────────────────────────────────────────

const RADIUS = {
  sm: '6px', //   chips, badges, inputs
  md: '10px', //  small cards, list items
  lg: '14px', //  form fields, secondary buttons
  xl: '20px', //  primary cards, drawers
} as const

const FONT_FAMILY =
  '"SF Pro Display", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'

// Institutional ease. Used by accordions, popovers, drawer collapses,
// hover lifts. Surfaced as --p-sliceal-motion-* so every screen consumes
// the same rhythm.
const MOTION = {
  fast: '160ms',
  base: '240ms',
  ease: 'cubic-bezier(0.22, 1, 0.36, 1)',
} as const

// ──────────────────────────────────────────────────────────────────────
// 4. INSTITUTIONAL — sidebar palette, dark-slate accents, decorative
// ──────────────────────────────────────────────────────────────────────
//
// Sidebar palette: intentionally dark in BOTH light and dark themes.
// The sidebar is the operator's command-line chrome — not a themeable
// surface. Tuned for long-session readability: refined slate base,
// indigo-tinted active state, near-white text.
const SIDEBAR = {
  bg: '#090939',
  surface: '#1C1C46',
  // 4-stop vertical gradient (top → bottom) for the sidebar chrome —
  // deep midnight indigo fading through slate-indigo into near-black.
  gradientStart: '#090939',
  gradientHigh: '#1B1B4D',
  gradientLow: '#1C1C46',
  gradientEnd: '#08081A',
  border: 'rgba(255, 255, 255, 0.07)',
  text: 'rgba(255, 255, 255, 0.88)',
  textMuted: 'rgba(255, 255, 255, 0.48)',
  icon: 'rgba(255, 255, 255, 0.72)',
  hover: 'rgba(255, 255, 255, 0.05)',
  activeBg: 'rgba(124, 92, 255, 0.14)',
  activeText: '#FFFFFF',
  accent: PRIMARY[500],
} as const

// Dark-slate accents — same family as SIDEBAR but with a touch more
// contrast. Used by avatar bubbles, dark page-header backgrounds,
// decorative dark surfaces across admin queues.
const SLATE = {
  slate: '#0F172A',
  slateHover: '#1E293B',
} as const

// Brand decorative — lavender wash used on landing-style surfaces inside
// the admin app (sign-up panel, navbar promotional band). Internal to the
// preset: `lavender` is referenced by the `signUpRight` alias below,
// while `primaryDeep` / `primaryGlow` are kept for future hero compositions.
// TODO — `primaryDeep`, `primaryGlow`, `landingNavbar` alias currently
// have zero consumers. Candidates for removal in a follow-up cleanup PR.
const BRAND_DECORATIVE = {
  lavender: '#EBE9FF',
  primaryDeep: '#24195F',
  primaryGlow: '#7A6BFF',
} as const

// Auth brand panel — the photographic showcase beside the sign-in / OTP
// forms. Every value here is a decorative overlay ON TOP OF A PHOTO, so no
// semantic surface/text token can express it: a flat scrim that drops the
// image contrast, a two-stop fade anchoring the headline block at the
// bottom, a translucent-white feature tile, and the sand accent the design
// uses for the panel subtitle. Surfaced as --p-sliceal-auth-brand-* so the
// auth module stays free of raw rgba / hex.
//
// `hintBg` / `hintText` are the indigo tint the design specifies for the
// "a verification code will be sent…" hint on the sign-in form. They sit
// off the brand violet ramp deliberately — the design calls for indigo
// here, so they live as auth-scoped tokens rather than a 6th brand shade.
const AUTH_BRAND = {
  scrim: 'rgba(0, 0, 0, 0.72)',
  fadeStart: 'rgba(0, 0, 0, 0)',
  fadeEnd: 'rgba(10, 10, 15, 0.78)',
  tile: 'rgba(255, 255, 255, 0.12)',
  subtitle: '#D9B98A',
  hintBg: '#EEF2FF',
  hintText: '#3730A3',
} as const

// ──────────────────────────────────────────────────────────────────────
// 5. BADGE PALETTE — branded pills (Badge molecule + Chip outline)
// ──────────────────────────────────────────────────────────────────────
//
// Decorative palette for product moments (RENTED, Premium, "Launches in
// 5 days"). NOT semantic — use SUCCESS / WARNING / DANGER / INFO for
// status feedback. Each entry lives on --p-sliceal-badge-*.

const BADGE = {
  blue: '#1570EF',
  navy: '#1849A9',
  cobalt: '#1754CF',
  green: '#017D28',
  teal: '#00765B',
  amber: '#FDB022',
  amberSoft: '#FFFAEB', //  near-duplicate of WARNING[50] — see TODO
  amberText: '#DC6803',
} as const

// Chip outline accents — single dark navy used for the outlined-navy chip
// variant. Sibling to BADGE but conceptually different (chip, not pill).
const CHIP = {
  darkNavy: '#194185',
} as const

// ──────────────────────────────────────────────────────────────────────
// 6. PRESET ASSEMBLY — wires everything into PrimeVue's shape
// ──────────────────────────────────────────────────────────────────────
//
// `slicealColors` carries the raw values, addressable internally as
// `{slicealColors.<key>}`. `slicealCssVars` promotes selected entries to
// `--p-sliceal-<kebab-key>` CSS variables that feature code consumes.
//
// Anything declared in `slicealColors` but NOT in `slicealCssVars` is
// internal-only (e.g. `lavender` is consumed via the `signUpRight` alias).

// Prefixes the keys of an object so each emerges as e.g. `sidebarBg`
// (from `prefix='sidebar'`, key='bg'). Keeps the inner const blocks
// shallow + readable while preserving the existing flat CSS-var names.
const prefixKeys = <T extends Record<string, string>>(
  prefix: string,
  obj: T,
): Record<string, string> =>
  Object.fromEntries(
    Object.entries(obj).map(([key, value]) => [
      `${prefix}${key.charAt(0).toUpperCase() + key.slice(1)}`,
      value,
    ]),
  )

const slicealColors = {
  // Brand decorative — internal-only references for the aliases below.
  ...BRAND_DECORATIVE,
  // Auto-emitted --p-sliceal-* CSS vars:
  ...prefixKeys('sidebar', SIDEBAR), //   sidebarBg, sidebarSurface, sidebarBorder, ...
  ...prefixKeys('dark', SLATE), //         darkSlate, darkSlateHover
  ...prefixKeys('badge', BADGE), //        badgeBlue, badgeNavy, ..., badgeAmberSoft
  ...prefixKeys('chip', CHIP), //          chipDarkNavy
  ...prefixKeys('authBrand', AUTH_BRAND), // authBrandScrim, authBrandFadeStart, ...
  ...prefixKeys('motion', MOTION), //      motionFast, motionBase, motionEase
} as const

// Keys that exist in `slicealColors` but are internal-only — they back
// the aliases below and don't get their own --p-sliceal-* CSS var.
const INTERNAL_ONLY = new Set(Object.keys(BRAND_DECORATIVE))

const slicealCssVars: Record<string, string> = {
  // Explicit aliases — these resolve to other slicealColors entries.
  landingNavbar: '{slicealColors.lavender}',
  signUpRight: '{slicealColors.lavender}',
  // Auto-promote every non-internal slicealColors entry to --p-sliceal-*.
  ...Object.fromEntries(
    Object.keys(slicealColors)
      .filter((key) => !INTERNAL_ONLY.has(key))
      .map((key) => [key, `{slicealColors.${key}}`]),
  ),
}

const SlicealPreset = definePreset(Aura, {
  semantic: {
    primary: PRIMARY,
    success: SUCCESS,
    warning: WARNING,
    danger: DANGER,
    info: INFO,
    colorScheme: {
      light: {
        text: LIGHT_TEXT,
        // Invalid form fields keep a MUTED placeholder, not a red one. Aura
        // tints the placeholder with the danger color on `:invalid` (light =
        // {red.600}), which makes the placeholder read as entered text. Point it
        // back at the normal placeholder token so only the border + error
        // message signal the invalid state. formField is defined PER colorScheme
        // in Aura, so the override must live inside each scheme — a top-level
        // semantic.formField override would not win.
        // Applies globally to Input / NumberInput / Select / DatePicker / Textarea.
        //
        // borderColor is pinned to {surface.200} (Aura ships {surface.300}) so a
        // field's resting edge matches --p-content-border-color — the same line
        // every Card and divider draws. This used to live in common.css as
        // `border-color: #e2e8f0 !important`, which also silently killed every
        // state border (filled / invalid / focus) on top of it.
        //
        // invalidBorderColor points at the brand danger ramp. Aura ships
        // {red.400} (#f87171), a tint light enough that an errored field reads
        // as merely hovered — and it never showed anyway while common.css was
        // forcing every border grey with !important.
        formField: {
          invalidPlaceholderColor: '{form.field.placeholder.color}',
          borderColor: '{surface.200}',
          hoverBorderColor: '{surface.300}',
          invalidBorderColor: '{danger.600}',
        },
      },
      dark: {
        text: DARK_TEXT,
        formField: {
          invalidPlaceholderColor: '{form.field.placeholder.color}',
          borderColor: DARK_SURFACE[200],
          hoverBorderColor: DARK_SURFACE[300],
          invalidBorderColor: '{danger.600}',
        },
        surface: DARK_SURFACE,
        primary: DARK_PRIMARY_TINTS,
        success: DARK_SUCCESS_TINTS,
        warning: DARK_WARNING_TINTS,
        danger: DARK_DANGER_TINTS,
        info: DARK_INFO_TINTS,
        content: {
          borderColor: DARK_SURFACE[200],
        },
      },
    },
  },

  primitive: {
    borderRadius: RADIUS,
    slicealColors,
    fontFamily: FONT_FAMILY,
  },

  extend: {
    sliceal: slicealCssVars,
  },
})

export default SlicealPreset
